import { useAuth, useUser } from '@dofleini/security';
import { proposalrequest } from '@mep/pae-commons';
import cloneDeep from 'lodash/cloneDeep';
import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useCrud, { showConfirm } from '@/hooks/useCrud';
import {
  CACHE_KEY_LIST_PROPOSAL_REQUEST,
  CACHE_KEY_PROPOSAL_REQUEST,
  ROUTE_USER_PATH_PROPOSAL_REQUEST
} from '@/modules/proposalRequest/contants/proposalRequest';
import { useGetOneProposalRequest } from '@/modules/proposalRequest/hooks/useProposalRequest';
import { handleIsSoleOwnerValue } from '@/modules/proposalRequest/hooks/useSocialDenominationType';
import ProposalRequestApiService from '@/modules/proposalRequest/services/ProposalRequestApiService';
import { proposalPayloadCheck } from '@/modules/proposalRequest/utils';
import { isEqualObjects } from '@/utils/utils';

const { PROPOSAL_STATUS } = proposalrequest;

const ProposalCreationContext = createContext();

const defaultData = {};

const { CNA } = proposalrequest;

function ProposalCreationProvider ({
  route = ROUTE_USER_PATH_PROPOSAL_REQUEST,
  initialStep,
  onCreateCompleted,
  isEdit,
  newProposalType,
  ...rest
}) {
  const { user } = useUser();
  const { data, isLoading: isLoadingData } = useGetOneProposalRequest({ id: rest?.id || rest?.proposal?.id }, {
    enabled: !!rest?.proposal?.id,
    refetchOnWindowFocus: false
  });
  const type = newProposalType || rest?.proposal?.subjectType || user?.subjectType || CNA;
  const [proposal, setProposal] = useState({
    subjectType: type,
    ...(rest.proposal || cloneDeep(defaultData))
  });

  const { reload } = useAuth();
  const [step, setStep] = useState(initialStep || 0);
  const { push } = useHistory();
  const { t } = useTranslation(['proposalRequest', 'alerts']);
  const {
    create,
    isCreating,
    update,
    isLoading,
    isUpdating
  } = useCrud(ProposalRequestApiService, CACHE_KEY_LIST_PROPOSAL_REQUEST, CACHE_KEY_PROPOSAL_REQUEST);

  useEffect(() => {
    const element = cloneDeep(data?.data || rest?.proposal);
    setProposal({
      ...element,
      isSoleOwner: handleIsSoleOwnerValue(element?.isSoleOwner, element?.owner?.isSoleOwner || user?.isSoleOwner),
      isRepresentative: element?.isRepresentative || element?.owner?.isRepresentative || user?.isRepresentative
    });
  }, [data?.data, rest?.proposal, user?.isRepresentative, user?.isSoleOwner]);

  const nextStep = useCallback(() => {
    setStep(prev => prev + 1);
  }, []);

  const prevStep = useCallback(() => {
    setStep(prev => prev - 1);
  }, []);

  const performSubmit = useCallback(async (payload, callback) => {
    // to be sure we are saving or updating
    if (proposal?.id)
      payload._id = proposal?.id;
    // set the type of the proposal
    if (!payload?.subjectType)
      payload.subjectType = type || proposal?.subjectType;
    // get the correct service
    const service = payload._id ? update : create;
    //update the organizations members
    if (!payload?.
      administrationControl)
      payload.administrationControl = proposal?.administrationControl;
    if (!payload.inspectionControl)
      payload.inspectionControl = proposal?.inspectionControl;
    //
    return new Promise((resolve, reject) => {
      service(proposalPayloadCheck(payload))
        .then(({ data }) => {
        // set the id to continue editing on the form
          callback && callback(data);
          //
          onCreateCompleted && onCreateCompleted(data);
          resolve(data);
        })
        .catch(e => reject(e));
    });
  }, [create, onCreateCompleted, proposal?.administrationControl, proposal?.id, proposal?.inspectionControl, proposal?.subjectType, type, update]);

  const onFinish = useCallback(async (payload) => {
    if ((!proposal.status || proposal.status === PROPOSAL_STATUS.DRAFT) && !isEdit)
      payload.status = PROPOSAL_STATUS.SENT;
    return performSubmit(payload, (data) => {
      setProposal(data);
      reload();
      push(`${route}/${data?.id}`);
    });
  }, [isEdit, performSubmit, proposal?.status, push, reload, route]);

  const onSave = useCallback((payload) => {
    if (!proposal?.status)
      payload.status = PROPOSAL_STATUS.DRAFT;
    return performSubmit(payload, (data) => {
      reload();
      setProposal(data);
    });
  }, [performSubmit, proposal?.status, reload]);

  const changeProposalAndNext = useCallback(async (payload) => {
    await onSave(payload);
    nextStep();
  }, [nextStep, onSave]);

  const changeProposalAndBack = useCallback((payload) => {
    !isEqualObjects(proposal, payload) ? showConfirm(
      t,
      t('discardChanges.content'),
      () => {
        prevStep();
      }
      , 'discardChanges.title'
    ) : prevStep();
  }, [prevStep, proposal, t]);

  return (
    <ProposalCreationContext.Provider value={{
      proposal,
      isCreating,
      isLoading,
      isLoadingData,
      isUpdating,
      setProposal,
      step,
      setStep,
      nextStep,
      prevStep,
      onFinish,
      onSave,
      changeProposalAndNext,
      changeProposalAndBack,
      newProposalType,
    }} {...rest} />
  );
}

function useProposalCreation () {
  const context = useContext(ProposalCreationContext);
  if (context === undefined) {
    throw new Error('useProposalCreation must be used within a ProposalCreationProvider');
  }
  const {
    proposal,
    isCreating,
    isLoading,
    isUpdating,
    setProposal,
    step,
    setStep,
    nextStep,
    prevStep,
    onFinish,
    onSave,
    changeProposalAndNext,
    changeProposalAndBack,
    newProposalType,
  } = context;

  return {
    proposal,
    isCreating,
    isLoading,
    isUpdating,
    setProposal,
    step,
    setStep,
    nextStep,
    prevStep,
    onFinish,
    onSave,
    changeProposalAndNext,
    changeProposalAndBack,
    newProposalType,
  };
}

ProposalCreationProvider.propTypes = {
  proposal: PropTypes.object,
  route: PropTypes.string,
  isEdit: PropTypes.bool,
  initialStep: PropTypes.number,
  onCreateCompleted: PropTypes.func,
  newProposalType: PropTypes.string
};

export { ProposalCreationProvider, useProposalCreation };
